import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { userService } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  MagnifyingGlassIcon 
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const UsersContainer = styled.div`
  /* Container styling handled by Layout component */
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const SearchBar = styled.div`
  position: relative;
  max-width: 400px;
  
  input {
    padding-left: 2.5rem;
    width: 100%;
  }
  
  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--gray-400);
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: ${props => props.$isDark ? '#1f2937' : 'white'};
  border-radius: 0.75rem;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  
  h2 {
    color: ${props => props.$isDark ? '#f9fafb' : 'var(--gray-900)'};
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  label {
    display: block;
    font-weight: 500;
    color: ${props => props.$isDark ? '#e5e7eb' : 'var(--gray-700)'};
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }
  
  .checkbox-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    
    input[type="checkbox"] {
      width: auto;
      margin: 0;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const UsersTable = styled.div`
  background: ${props => props.$isDark ? '#1f2937' : 'white'};
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.$isDark ? '#374151' : 'var(--gray-200)'};
`;

const TableHeader = styled.div`
  background-color: ${props => props.$isDark ? '#374151' : 'var(--gray-50)'};
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${props => props.$isDark ? '#4b5563' : 'var(--gray-200)'};
  font-weight: 600;
  color: ${props => props.$isDark ? '#e5e7eb' : 'var(--gray-700)'};
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 120px;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr 80px;
    
    .hide-mobile {
      display: none;
    }
  }
`;

const TableRow = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${props => props.$isDark ? '#374151' : 'var(--gray-200)'};
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 120px;
  gap: 1rem;
  align-items: center;
  transition: background-color 0.2s;
  color: ${props => props.$isDark ? '#f9fafb' : 'inherit'};
  
  &:hover {
    background-color: ${props => props.$isDark ? '#374151' : 'var(--gray-50)'};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr 80px;
    
    .hide-mobile {
      display: none;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  button {
    padding: 0.375rem;
    border: 1px solid ${props => props.$isDark ? '#4b5563' : 'var(--gray-300)'};
    border-radius: 0.375rem;
    background: ${props => props.$isDark ? '#374151' : 'white'};
    color: ${props => props.$isDark ? '#e5e7eb' : 'inherit'};
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover.edit-btn {
      background-color: var(--primary-50);
      border-color: var(--primary-600);
      color: var(--primary-600);
    }
    
    &:hover.delete-btn {
      background-color: var(--primary-50);
      border-color: var(--primary-600);
      color: var(--primary-600);
    }
    
    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  background: ${props => props.$isDark ? '#1f2937' : 'white'};
  border-radius: 0.5rem;
  
  .pagination-info {
    color: ${props => props.$isDark ? '#9ca3af' : 'var(--gray-600)'};
    font-size: 0.875rem;
  }
  
  .pagination-buttons {
    display: flex;
    gap: 0.5rem;
  }
`;

function Users() {
  const { user: currentUser } = useAuth();
  const { isDark } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    fetchUsers();
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getUsers(currentPage, 20);
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Fetch users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    reset();
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setValue('first_name', user.first_name);
    setValue('last_name', user.last_name);
    setValue('email', user.email);
    setValue('is_admin', user.is_admin);
    setValue('is_active', user.is_active);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      if (editingUser) {
        await userService.updateUser(editingUser.id, data);
        toast.success('User updated successfully');
      } else {
        await userService.createUser(data);
        toast.success('User created successfully');
      }
      
      closeModal();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      try {
        await userService.deleteUser(userId);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Delete failed');
      }
    }
  };

  const filteredUsers = users.filter(user =>
    user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!currentUser?.is_admin) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--primary-600)' }}>
        Access denied. Admin privileges required.
      </div>
    );
  }

  return (
    <UsersContainer>
      <Header>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: isDark ? '#f9fafb' : 'var(--gray-900)' }}>
          Users Management
        </h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <SearchBar>
            <MagnifyingGlassIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
            />
          </SearchBar>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <PlusIcon style={{ width: '1rem', height: '1rem' }} />
            Add User
          </button>
        </div>
      </Header>

      <UsersTable $isDark={isDark}>
        <TableHeader $isDark={isDark}>
          <div>Name</div>
          <div>Email</div>
          <div className="hide-mobile">Status</div>
          <div className="hide-mobile">Role</div>
          <div className="hide-mobile">Created</div>
          <div>Actions</div>
        </TableHeader>
        
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#9ca3af' : 'inherit' }}>Loading...</div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#9ca3af' : 'var(--gray-500)' }}>
            No users found
          </div>
        ) : (
          filteredUsers.map((user) => (
            <TableRow key={user.id} $isDark={isDark}>
              <div style={{ fontWeight: '500' }}>
                {user.first_name} {user.last_name}
              </div>
              <div style={{ color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>{user.email}</div>
              <div className="hide-mobile">
                <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="hide-mobile">
                <span className={`badge ${user.is_admin ? 'badge-info' : 'badge-warning'}`}>
                  {user.is_admin ? 'Admin' : 'User'}
                </span>
              </div>
              <div className="hide-mobile" style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                {format(new Date(user.created_at), 'MMM d, yyyy')}
              </div>
              <ActionButtons $isDark={isDark}>
                <button
                  className="edit-btn"
                  onClick={() => openEditModal(user)}
                  title="Edit user"
                >
                  <PencilIcon />
                </button>
                {currentUser.id !== user.id && (
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(user.id, `${user.first_name} ${user.last_name}`)}
                    title="Delete user"
                  >
                    <TrashIcon />
                  </button>
                )}
              </ActionButtons>
            </TableRow>
          ))
        )}
      </UsersTable>

      {pagination.total > 0 && (
        <Pagination $isDark={isDark}>
          <div className="pagination-info">
            Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
            {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
            {pagination.total} users
          </div>
          <div className="pagination-buttons">
            <button
              className="btn btn-secondary btn-sm"
              disabled={pagination.page <= 1}
              onClick={() => setCurrentPage(pagination.page - 1)}
            >
              Previous
            </button>
            <button
              className="btn btn-secondary btn-sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setCurrentPage(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </Pagination>
      )}

      <Modal show={showModal} onClick={closeModal}>
        <ModalContent $isDark={isDark} onClick={(e) => e.stopPropagation()}>
          <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
          
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup $isDark={isDark}>
              <label htmlFor="first_name">First Name *</label>
              <input
                type="text"
                {...register('first_name', { required: 'First name is required' })}
                style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
              />
              {errors.first_name && (
                <span style={{ color: 'var(--primary-600)', fontSize: '0.875rem' }}>
                  {errors.first_name.message}
                </span>
              )}
            </FormGroup>

            <FormGroup $isDark={isDark}>
              <label htmlFor="last_name">Last Name *</label>
              <input
                type="text"
                {...register('last_name', { required: 'Last name is required' })}
                style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
              />
              {errors.last_name && (
                <span style={{ color: 'var(--primary-600)', fontSize: '0.875rem' }}>
                  {errors.last_name.message}
                </span>
              )}
            </FormGroup>

            <FormGroup $isDark={isDark}>
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email format'
                  }
                })}
                disabled={!!editingUser}
                style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
              />
              {errors.email && (
                <span style={{ color: 'var(--primary-600)', fontSize: '0.875rem' }}>
                  {errors.email.message}
                </span>
              )}
            </FormGroup>

            {!editingUser && (
              <FormGroup $isDark={isDark}>
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  {...register('password', { 
                    required: !editingUser ? 'Password is required' : false,
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters'
                    }
                  })}
                  style={{ backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                />
                {errors.password && (
                  <span style={{ color: 'var(--primary-600)', fontSize: '0.875rem' }}>
                    {errors.password.message}
                  </span>
                )}
              </FormGroup>
            )}

            <FormGroup $isDark={isDark}>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  {...register('is_admin')}
                />
                <label htmlFor="is_admin">Admin User</label>
              </div>
            </FormGroup>

            <FormGroup $isDark={isDark}>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  defaultChecked={true}
                  {...register('is_active')}
                />
                <label htmlFor="is_active">Active User</label>
              </div>
            </FormGroup>

            <ButtonGroup>
              <button type="button" className="btn btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingUser ? 'Update' : 'Create'} User
              </button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>
    </UsersContainer>
  );
}

export default Users;